import React from "react";
import { TextField } from "@mui/material";
import {
  landingCopyConstraint,
  landingCopyFieldState,
  landingCopyHelperText,
} from "../copyLimits";
import { registryFieldError } from "./inspectorModel";
import { isExperienceDefaultOverridePath } from "../../landingEditor/siteExperienceDefaults";

export default function LandingCopyTextField({
  field,
  path: explicitPath,
  form,
  baselineForm,
  value,
  onCommit,
  commitMode = "blur",
  trimOnCommit = false,
  commitOnUnmount = false,
  helperText = "",
  onKeyDown,
  ...props
}) {
  const path = explicitPath || field?.path || "";
  const current = String(value ?? "");
  const [draft, setDraft] = React.useState(current);
  const draftRef = React.useRef(current);
  const commitRef = React.useRef(() => {});
  const constraint = landingCopyConstraint(path);

  React.useEffect(() => {
    draftRef.current = current;
    setDraft(current);
  }, [current, path]);

  const commit = React.useCallback((rawValue = draft) => {
    const nextValue = trimOnCommit ? String(rawValue ?? "").trim() : String(rawValue ?? "");
    if (constraint) {
      if (nextValue !== current) onCommit?.(nextValue);
      return;
    }
    const error = field ? registryFieldError(field, nextValue) : "";
    if (!error && nextValue !== current) onCommit?.(nextValue);
  }, [constraint, current, draft, field, onCommit, trimOnCommit]);

  commitRef.current = commit;

  React.useEffect(() => () => {
    if (commitOnUnmount) commitRef.current(draftRef.current);
  }, [commitOnUnmount, path]);

  const state = constraint
    ? landingCopyFieldState({ path, value: draft, form, baselineForm })
    : null;
  const fallbackError = !constraint && field ? registryFieldError(field, draft) : "";
  const experienceAutomaticPath = isExperienceDefaultOverridePath(path);
  const experienceCustomized = experienceAutomaticPath
    && Array.isArray(form?.experienceDefaults?.overrides)
    && form.experienceDefaults.overrides.includes(path);
  const contextualHelper = experienceAutomaticPath
    ? experienceCustomized
      ? "Personalizado. Si lo vaciás, vuelve al texto automático de la experiencia activa."
      : "Automático para la experiencia activa. Si lo vaciás, conserva el texto automático."
    : helperText;
  const resolvedHelper = state
    ? landingCopyHelperText(state, contextualHelper)
    : fallbackError || contextualHelper;

  return (
    <TextField
      {...props}
      value={draft}
      error={state?.invalid === true || Boolean(fallbackError)}
      helperText={resolvedHelper || undefined}
      inputProps={{
        ...(props.inputProps || {}),
        ...(!constraint && Number.isFinite(field?.maxLength) ? { maxLength: field.maxLength } : {}),
      }}
      FormHelperTextProps={{
        ...(props.FormHelperTextProps || {}),
        ...(state?.legacyUnchanged ? { sx: { color: "warning.main", ...(props.FormHelperTextProps?.sx || {}) } } : {}),
      }}
      onChange={(event) => {
        const nextValue = event.target.value;
        draftRef.current = nextValue;
        setDraft(nextValue);
        props.onChange?.(event);
        if (commitMode === "change") onCommit?.(nextValue);
      }}
      onBlur={(event) => {
        if (commitMode !== "change") commit(event.target.value);
        props.onBlur?.(event);
      }}
      onKeyDown={(event) => {
        if (onKeyDown) onKeyDown(event);
        if (event.defaultPrevented) return;
        if (commitMode === "blur" && event.key === "Enter" && !props.multiline && !event.nativeEvent?.isComposing) {
          event.preventDefault();
          event.currentTarget.blur();
        }
      }}
    />
  );
}
